<template>
  <div class="min-h-screen bg-gray-50">
    <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <!-- Header -->
      <div class="mb-6 flex justify-between items-center">
        <h1 class="text-3xl font-bold text-gray-900">Disponibilidades</h1>
      </div>

      <!-- Filtro por imóvel -->
      <div class="bg-white rounded-lg shadow-sm p-6 mb-6">
        <div class="w-full md:w-80">
          <PropertySelectAsync
            v-model="selectedPropertyId"
            label="Imóvel"
            placeholder=""
            :required="true"
          />
        </div>
      </div>

      <!-- Calendários -->
      <div class="bg-white rounded-xl shadow p-6">
        <div class="flex flex-col md:flex-row md:items-center md:justify-between gap-3 mb-4">
          <div class="flex items-center gap-3">
            <span class="text-sm font-medium text-gray-700">Ano</span>
            <button
              type="button"
              @click="changeYear(-1)"
              class="p-2 hover:bg-gray-100 rounded-lg transition"
            >
              <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 19l-7-7 7-7" />
              </svg>
            </button>
            <h2 class="text-xl font-semibold text-gray-900">
              {{ currentYear }}
            </h2>
            <button
              type="button"
              @click="changeYear(1)"
              class="p-2 hover:bg-gray-100 rounded-lg transition"
            >
              <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7" />
              </svg>
            </button>
          </div>

          <div class="flex flex-wrap items-center gap-3 md:gap-4 text-xs text-gray-600">
            <div class="flex items-center gap-1">
              <span class="inline-block w-3 h-3 rounded-sm bg-indigo-100 border border-indigo-300"></span>
              <span>Hospedagem</span>
            </div>
            <div class="flex items-center gap-1">
              <span class="inline-block w-3 h-3 rounded-sm bg-amber-100 border border-amber-300"></span>
              <span>Serviço</span>
            </div>
            <div class="flex items-center gap-1">
              <span class="inline-block w-3 h-3 rounded-sm bg-green-100 border border-green-300"></span>
              <span>Disponível</span>
            </div>
            <div class="flex items-center gap-1">
              <span class="inline-block w-3 h-3 rounded-sm bg-gray-100 border border-gray-300"></span>
              <span>Inativo</span>
            </div>
          </div>
        </div>

        <div v-if="!selectedPropertyId" class="text-gray-500 text-sm">
          Selecione um imóvel acima para ver as disponibilidades.
        </div>
        <div v-else class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          <AvailabilityMonthCalendar
            v-for="month in 12"
            :key="month"
            :year="currentYear"
            :month-index="month - 1"
            :unavailable-dates="unavailableDates"
            :day-labels="dayLabels"
            :movements="movementsForMonth(month - 1)"
            :property-id="selectedPropertyId"
          />
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import PropertySelectAsync from './PropertySelectAsync.vue'
import AvailabilityMonthCalendar from './AvailabilityMonthCalendar.vue'
import { useApi } from '../composables/useApi.js'

export default {
  name: 'Availabilities',
  components: {
    PropertySelectAsync,
    AvailabilityMonthCalendar
  },
  data() {
    const masterCode = localStorage.getItem('masterCode') || 'default'
    const lastPropertyKey = `tripday:last_selected_property_id:${masterCode}`
    const savedPropertyId = localStorage.getItem(lastPropertyKey)

    return {
      selectedPropertyId: savedPropertyId || '',
      currentYear: new Date().getFullYear(),
      unavailableDates: new Set(),
      dayLabels: {},
      movements: []
    }
  },
  watch: {
    selectedPropertyId: {
      immediate: true,
      handler(newVal) {
        const masterCode = localStorage.getItem('masterCode') || 'default'
        const lastPropertyKey = `tripday:last_selected_property_id:${masterCode}`
        if (newVal === null || newVal === undefined || newVal === '') {
          localStorage.removeItem(lastPropertyKey)
        } else {
          localStorage.setItem(lastPropertyKey, String(newVal))
        }

        this.loadAvailability()
      }
    }
  },
  methods: {
    changeYear(delta) {
      this.currentYear += delta
      this.loadAvailability()
    },
    async loadAvailability() {
      if (!this.selectedPropertyId) return
      const { get } = useApi()
      const { data, error } = await get(`/api/v1/properties/${this.selectedPropertyId}/availability?year=${this.currentYear}`)
      if (error || !data) return

      const dates = new Set()
      ;(data.unavailable_dates || []).forEach((d) => dates.add(d))
      this.unavailableDates = dates
      this.dayLabels = data.day_labels || {}
      this.movements = data.movements || []
    },
    movementsForMonth(monthIndex) {
      if (!this.movements || !Array.isArray(this.movements)) return []

      const monthStart = new Date(this.currentYear, monthIndex, 1)
      const monthEnd = new Date(this.currentYear, monthIndex + 1, 0)

      return this.movements.filter((m) => {
        if (!m.check_in_date || !m.check_out_date) return false

        const [ciY, ciM, ciD] = m.check_in_date.split('-').map((v) => parseInt(v, 10))
        const [coY, coM, coD] = m.check_out_date.split('-').map((v) => parseInt(v, 10))
        if (!ciY || !coY) return false

        const checkIn = new Date(ciY, ciM - 1, ciD)
        const checkOut = new Date(coY, coM - 1, coD)

        // sobreposição entre [checkIn, checkOut] e [monthStart, monthEnd]
        return checkOut >= monthStart && checkIn <= monthEnd
      })
    }
  }
}
</script>

